import { createContext, useContext, useState } from "react";
import { toast } from "sonner";
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { api, errMsg } from "@/lib/api";
import { SECTOR_LINKS } from "@/lib/sectors";

const QuoteContext = createContext({ openQuote: () => {} });
export const useQuote = () => useContext(QuoteContext);

const EMPTY = { organisation: "", contact_name: "", email: "", phone: "", sites: "", meals_per_day: "", message: "" };

export const QuoteProvider = ({ children }) => {
  const [state, setState] = useState({ open: false, sector: "care-homes", plan: "" });
  const [form, setForm] = useState(EMPTY);
  const [busy, setBusy] = useState(false);
  const openQuote = (sector = "care-homes", plan = "") => setState({ open: true, sector, plan });
  const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      await api.post("/enquiries", {
        ...form,
        sector: state.sector,
        plan: state.plan || null,
        phone: form.phone || null,
        message: form.message || null,
        sites: form.sites ? Number(form.sites) : null,
        meals_per_day: form.meals_per_day ? Number(form.meals_per_day) : null,
      });
      toast.success("Thank you. We'll be in touch within one working day.");
      setForm(EMPTY);
      setState((s) => ({ ...s, open: false }));
    } catch (err) {
      toast.error(errMsg(err));
    } finally {
      setBusy(false);
    }
  };

  return (
    <QuoteContext.Provider value={{ openQuote }}>
      {children}
      <Dialog open={state.open} onOpenChange={(open) => setState((s) => ({ ...s, open }))}>
        <DialogContent className="max-h-[92vh] overflow-y-auto bg-[#FAF8F5] sm:max-w-xl" data-lenis-prevent data-testid="quote-dialog">
          <DialogHeader>
            <p className="eyebrow text-[#9a7614]">Contract enquiry</p>
            <DialogTitle className="font-display text-3xl">Request a tailored quote</DialogTitle>
            <DialogDescription>Tell us about your site and we'll prepare a proposal and arrange a tasting.</DialogDescription>
          </DialogHeader>
          <form onSubmit={submit} className="grid gap-4 sm:grid-cols-2" data-testid="quote-form">
            <div>
              <label className="label">Sector</label>
              <select className="field" value={state.sector} onChange={(e) => setState((s) => ({ ...s, sector: e.target.value }))} data-testid="quote-sector-select">
                {SECTOR_LINKS.map((s) => <option key={s.slug} value={s.slug}>{s.label}</option>)}
              </select>
            </div>
            <div>
              <label className="label">Plan of interest</label>
              <select className="field" value={state.plan} onChange={(e) => setState((s) => ({ ...s, plan: e.target.value }))} data-testid="quote-plan-select">
                <option value="">Not sure yet</option>
                <option>Essential</option>
                <option>Enhanced</option>
                <option>Signature</option>
              </select>
            </div>
            <div className="sm:col-span-2">
              <label className="label">Organisation</label>
              <input required className="field" value={form.organisation} onChange={set("organisation")} data-testid="quote-organisation-input" />
            </div>
            <div>
              <label className="label">Your name</label>
              <input required className="field" value={form.contact_name} onChange={set("contact_name")} data-testid="quote-name-input" />
            </div>
            <div>
              <label className="label">Email</label>
              <input required type="email" className="field" value={form.email} onChange={set("email")} data-testid="quote-email-input" />
            </div>
            <div>
              <label className="label">Phone</label>
              <input className="field" value={form.phone} onChange={set("phone")} data-testid="quote-phone-input" />
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div>
                <label className="label">Sites</label>
                <input type="number" min="1" className="field" value={form.sites} onChange={set("sites")} data-testid="quote-sites-input" />
              </div>
              <div>
                <label className="label">Meals/day</label>
                <input type="number" min="1" className="field" value={form.meals_per_day} onChange={set("meals_per_day")} data-testid="quote-meals-input" />
              </div>
            </div>
            <div className="sm:col-span-2">
              <label className="label">Anything else?</label>
              <textarea rows={3} className="field" value={form.message} onChange={set("message")} data-testid="quote-message-input" />
            </div>
            <button disabled={busy} className="btn-ink sm:col-span-2 justify-center" data-testid="quote-submit-button">
              {busy ? "Sending…" : "Send enquiry"}
            </button>
          </form>
        </DialogContent>
      </Dialog>
    </QuoteContext.Provider>
  );
};
